import React, { useState, useEffect } from 'react';
import {
  Paper, Box, Typography, Grid, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, CircularProgress, Alert,
  Divider, IconButton, Chip, Tooltip
} from '@mui/material';
import { Edit as EditIcon, Save as SaveIcon, Cancel as CancelIcon, Email as EmailIcon, Phone as PhoneIcon, Home as HomeIcon, ContentCopy as CopyIcon } from '@mui/icons-material';
import api from '../api/api'; // Ajusta la ruta según tu proyecto

// ==========================================
// HELPERS DE FORMATEO (Extraídos de EditarCliente)
// ==========================================
const formatNit = (value) => {
  if (!value) return '';
  const digits = String(value).replace(/\D/g, '');
  if (digits.length !== 14) return digits;
  return `${digits.slice(0,4)}-${digits.slice(4,10)}-${digits.slice(10,13)}-${digits.slice(13)}`;
};

const formatDui = (value) => {
  if (!value) return '';
  const digits = String(value).replace(/\D/g, '');
  if (digits.length !== 9) return digits;
  return `${digits.slice(0,8)}-${digits.slice(8)}`;
};

const validateEmail = (email) => {
  if (!email) return '';
  const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return regex.test(email) ? '' : 'Formato de email inválido';
};

const ClienteInfoCard = ({ idCliente, onClienteUpdated }) => {
  const [cliente, setCliente] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Formulario de edición
  const [formData, setFormData] = useState({
    nombre_cliente: '',
    dui: '',
    nit: '',
    nrc: '',
    pasaporte: '',
    carnet_de_residencia: '',
    doc_id_cliente: '',
    email_cliente: '',
    telefonos_cliente: '',
    direccion_cliente: '',
  });

  const [docErrors, setDocErrors] = useState({ dui: '', nit: '', nrc: '', email: '' });

  // Cargar datos del cliente
  const fetchCliente = async () => {
    if (!idCliente) return;
    try {
      setLoading(true);
      const res = await api.get(`/clientes/${idCliente}`);
      setCliente(res.data);
      setFormData({
        nombre_cliente: res.data.nombre_cliente || '',
        dui: res.data.dui || '',
        nit: res.data.nit || '',
        nrc: res.data.nrc || '',
        pasaporte: res.data.pasaporte || '',
        carnet_de_residencia: res.data.carnet_de_residencia || '',
        doc_id_cliente: res.data.doc_id_cliente || '',
        email_cliente: res.data.email_cliente || '',
        telefonos_cliente: res.data.telefonos_cliente || '',
        direccion_cliente: res.data.direccion_cliente || '',
      });
    } catch (err) {
      console.error('Error al cargar datos del cliente:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = (text, onlyDigits = false) => {
    if (!text) return;
    const cleanText = onlyDigits ? String(text).replace(/\D/g, '') : text;
    navigator.clipboard.writeText(cleanText);
  };

  useEffect(() => {
    if (idCliente) {
      fetchCliente();
    }
  }, [idCliente]);

  // Manejadores de cambios
  const handleDuiChange = (e) => {
    const raw = e.target.value.replace(/\D/g, '').slice(0, 9);
    setFormData(prev => ({ ...prev, dui: raw }));
    setDocErrors(prev => ({ ...prev, dui: raw && raw.length !== 9 ? 'El DUI debe tener 9 dígitos' : '' }));
  };

  const handleNitChange = (e) => {
    const raw = e.target.value.replace(/\D/g, '').slice(0, 14);
    setFormData(prev => ({ ...prev, nit: raw }));
    setDocErrors(prev => ({ ...prev, nit: raw && raw.length !== 14 ? 'El NIT debe tener 14 dígitos' : '' }));
  };

  const handleNrcChange = (e) => {
    const value = e.target.value.replace(/\s/g, '');
    setFormData(prev => ({ ...prev, nrc: value }));
    setDocErrors(prev => ({ ...prev, nrc: /^\d+$/.test(value) || !value ? '' : 'NRC debe ser numérico' }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');

      const payload = {
        ...cliente,
        nombre_cliente: formData.nombre_cliente.trim(),
        dui: formData.dui || null,
        nit: formData.nit || null,
        nrc: formData.nrc || null,
        pasaporte: formData.pasaporte || null,
        carnet_de_residencia: formData.carnet_de_residencia || null,
        doc_id_cliente: formData.doc_id_cliente || null,
        email_cliente: formData.email_cliente || null,
        telefonos_cliente: formData.telefonos_cliente || null,
        direccion_cliente: formData.direccion_cliente || null,
      };

      await api.put(`/clientes/${idCliente}`, payload);
      await fetchCliente(); // Refrescar vista local
      if (onClienteUpdated) onClienteUpdated(); // Notificar al padre si es necesario
      setOpenModal(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al guardar los datos del cliente');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <CircularProgress size={24} sx={{ my: 2 }} />;
  if (!cliente) return <Alert severity="warning">No se encontró información del cliente.</Alert>;

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
      {/* CABECERA CON BOTÓN EDITAR */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" fontWeight="bold" color="primary">
          Datos del Cliente / Receptor
        </Typography>
        <Button
          startIcon={<EditIcon />}
          variant="outlined"
          size="small"
          onClick={() => setOpenModal(true)}
        >
          Editar Cliente
        </Button>
      </Box>

      {/* VISTA RESUMEN EN EL ALBARÁN */}
<Grid container spacing={2}>
  <Grid item xs={12} sm={6}>
    <Typography variant="subtitle1" fontWeight="bold">
      {cliente.nombre_cliente}
    </Typography>
    
    {/* Teléfono */}
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Typography variant="body2" color="text.secondary">
        <PhoneIcon fontSize="inherit" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
        {cliente.telefonos_cliente || 'Sin teléfono'}
      </Typography>
      {cliente.telefonos_cliente && (
        <Tooltip title="Copiar Teléfono">
          <IconButton size="small" onClick={() => handleCopy(cliente.telefonos_cliente)}>
            <CopyIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>

    {/* Email */}
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Typography variant="body2" color="text.secondary">
        <EmailIcon fontSize="inherit" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
        {cliente.email_cliente || 'Sin correo'}
      </Typography>
      {cliente.email_cliente && (
        <Tooltip title="Copiar Correo">
          <IconButton size="small" onClick={() => handleCopy(cliente.email_cliente)}>
            <CopyIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>

    {/* Dirección */}
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Typography variant="body2" color="text.secondary">
        <HomeIcon fontSize="inherit" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
        {cliente.direccion_cliente || 'Sin dirección registrada'}
      </Typography>
      {cliente.direccion_cliente && (
        <Tooltip title="Copiar Dirección">
          <IconButton size="small" onClick={() => handleCopy(cliente.direccion_cliente)}>
            <CopyIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  </Grid>

          {/* DOCUMENTOS Y TRIBUTOS */}
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" fontWeight="bold" color="text.secondary" gutterBottom>
              Documentos de Identificación:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              
              {cliente.dui && (
                <Chip
                  label={`DUI: ${formatDui(cliente.dui)}`}
                  size="small"
                  variant="outlined"
                  color="info"
                  onDelete={() => handleCopy(cliente.dui, true)}
                  deleteIcon={
                    <Tooltip title="Copiar DUI solo números">
                      <CopyIcon />
                    </Tooltip>
                  }
                />
              )}

              {cliente.nit && (
                <Chip
                  label={`NIT: ${formatNit(cliente.nit)}`}
                  size="small"
                  variant="outlined"
                  color="info"
                  onDelete={() => handleCopy(cliente.nit, true)}
                  deleteIcon={
                    <Tooltip title="Copiar NIT solo números">
                      <CopyIcon />
                    </Tooltip>
                  }
                />
              )}

              {cliente.nrc && (
                <Chip
                  label={`NRC: ${cliente.nrc}`}
                  size="small"
                  variant="outlined"
                  color="secondary"
                  onDelete={() => handleCopy(cliente.nrc, true)}
                  deleteIcon={
                    <Tooltip title="Copiar NRC solo números">
                      <CopyIcon />
                    </Tooltip>
                  }
                />
              )}

              {cliente.doc_id_cliente && (
                <Chip
                  label={`Otro Doc: ${cliente.doc_id_cliente}`}
                  size="small"
                  variant="outlined"
                  onDelete={() => handleCopy(cliente.doc_id_cliente, true)}
                  deleteIcon={
                    <Tooltip title="Copiar Documento solo números">
                      <CopyIcon />
                    </Tooltip>
                  }
                />
              )}

              {cliente.pasaporte && (
                <Chip
                  label={`Pasaporte: ${cliente.pasaporte}`}
                  size="small"
                  variant="outlined"
                  onDelete={() => handleCopy(cliente.pasaporte)}
                  deleteIcon={
                    <Tooltip title="Copiar Pasaporte">
                      <CopyIcon />
                    </Tooltip>
                  }
                />
              )}

              {cliente.carnet_de_residencia && (
                <Chip
                  label={`Carnet Res.: ${cliente.carnet_de_residencia}`}
                  size="small"
                  variant="outlined"
                  onDelete={() => handleCopy(cliente.carnet_de_residencia)}
                  deleteIcon={
                    <Tooltip title="Copiar Carnet">
                      <CopyIcon />
                    </Tooltip>
                  }
                />
              )}

              {!cliente.dui && !cliente.nit && !cliente.nrc && !cliente.pasaporte && !cliente.carnet_de_residencia && !cliente.doc_id_cliente && (
                <Typography variant="caption" color="text.secondary">
                  Sin documentos de identidad registrados
                </Typography>
              )}
            </Box>
          </Grid>
        </Grid>

      {/* DIALOG / MODAL DE EDICIÓN RÁPIDA */}
      <Dialog open={openModal} onClose={() => setOpenModal(false)} fullWidth maxWidth="md">
        <DialogTitle>Editar Información del Cliente</DialogTitle>
        <DialogContent dividers>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Nombre completo *"
                fullWidth
                value={formData.nombre_cliente}
                onChange={e => setFormData({ ...formData, nombre_cliente: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Email"
                fullWidth
                value={formData.email_cliente}
                onChange={e => {
                  const val = e.target.value.trimStart();
                  setFormData({ ...formData, email_cliente: val });
                  setDocErrors(prev => ({ ...prev, email: validateEmail(val) }));
                }}
                error={!!docErrors.email}
                helperText={docErrors.email}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Teléfono"
                fullWidth
                value={formData.telefonos_cliente}
                onChange={e => setFormData({ ...formData, telefonos_cliente: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="DUI"
                fullWidth
                value={formatDui(formData.dui)}
                onChange={handleDuiChange}
                error={!!docErrors.dui}
                helperText={docErrors.dui || 'Formato: 8 dígitos + 1 guion'}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="NIT"
                fullWidth
                value={formatNit(formData.nit)}
                onChange={handleNitChange}
                error={!!docErrors.nit}
                helperText={docErrors.nit || 'Formato: 14 dígitos'}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="NRC"
                fullWidth
                value={formData.nrc}
                onChange={handleNrcChange}
                error={!!docErrors.nrc}
                helperText={docErrors.nrc}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Pasaporte"
                fullWidth
                value={formData.pasaporte}
                onChange={e => setFormData({ ...formData, pasaporte: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Carnet Residencia"
                fullWidth
                value={formData.carnet_de_residencia}
                onChange={e => setFormData({ ...formData, carnet_de_residencia: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Otro Documento (PTRPS)"
                fullWidth
                value={formData.doc_id_cliente}
                onChange={e => setFormData({ ...formData, doc_id_cliente: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Dirección"
                fullWidth
                multiline
                rows={2}
                value={formData.direccion_cliente}
                onChange={e => setFormData({ ...formData, direccion_cliente: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenModal(false)} startIcon={<CancelIcon />}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving || !!docErrors.dui || !!docErrors.nit || !!docErrors.nrc}
            startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
          >
            Guardar Cambios
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default ClienteInfoCard;